import React from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Download, X } from 'lucide-react';

/**
 * Modal for selecting a custom time period and generating a PDF report.
 * Extracted from Visits.jsx.
 */
export default function DateRangeReportModal({
  open,
  onClose,
  dateRangeReport,
  setDateRangeReport,
  dateRangeType,
  setDateRangeType,
  customStartDate,
  setCustomStartDate,
  customEndDate,
  setCustomEndDate,
  onGenerate,
}) {
  if (!open) return null;

  const periods = [
    { key: 'weekly', label: 'Weekly' },
    { key: 'monthly', label: 'Monthly' },
    { key: 'quarterly', label: 'Quarterly' },
    { key: 'annual', label: 'Annual' },
    { key: 'custom', label: 'Custom' },
  ];

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <Card className="w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="date-range-modal">
        <div className="p-5 border-b flex items-center justify-between">
          <h2 className="font-heading text-lg font-semibold flex items-center gap-2">
            <Download size={20} /> Report by Period
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X size={16} />
          </Button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium">Report Type</label>
            <select
              className="w-full rounded-md border px-3 py-2 text-sm"
              value={dateRangeReport}
              onChange={(e) => setDateRangeReport(e.target.value)}
              data-testid="date-range-report-type"
            >
              <option value="therapists">Therapists</option>
              <option value="agencies">Agencies</option>
              <option value="lateNotes">Late Notes</option>
              <option value="missed">Missed Visits</option>
            </select>
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium">Time Period</label>
            <div className="grid grid-cols-5 gap-1">
              {periods.map((p) => (
                <Button
                  key={p.key}
                  size="sm"
                  variant={dateRangeType === p.key ? 'default' : 'outline'}
                  onClick={() => setDateRangeType(p.key)}
                  className="text-xs"
                  data-testid={`period-${p.key}`}
                >
                  {p.label}
                </Button>
              ))}
            </div>
          </div>

          {dateRangeType === 'custom' && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-xs font-medium">Start Date</label>
                <Input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  data-testid="custom-start-date"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium">End Date</label>
                <Input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  data-testid="custom-end-date"
                />
              </div>
            </div>
          )}

          <p className="text-xs text-muted-foreground">
            {dateRangeType === 'weekly' && 'Last completed week (Sun–Sat)'}
            {dateRangeType === 'monthly' && 'Last full calendar month'}
            {dateRangeType === 'quarterly' && 'Last full quarter'}
            {dateRangeType === 'annual' && 'Last full calendar year'}
            {dateRangeType === 'custom' && 'Select your own date range'}
          </p>
        </div>

        <div className="p-5 border-t flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" onClick={onGenerate} className="gap-1.5" data-testid="generate-period-report">
            <Download size={14} /> Generate PDF
          </Button>
        </div>
      </Card>
    </div>
  );
}
